import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import PT from 'prop-types';
import { useUID, TaktIdConsumer, createStormTaktId, TextAreaFormGroup, InlineMessage } from '@amzn/storm-ui';
import { g as getCharactersCount } from './utils/getCharactersCount-D5D88kic.js';
import { n as noop } from '../../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

// copied from packages/storm-ui/src/TextAreaFormGroup/TextAreaFormGroup.tsx
const textAreaPropTypes = {
  /**
   * The page unique identifier of the TextArea input.
   */
  id: PT.string.isRequired,
  /**
   * The class name passed through to the wrapper `div`.
   */
  className: PT.string,
  /**
   * Disables the TextAreaFormGroup.
   */
  disabled: PT.bool,
  /**
   * The function called when any changes are made within the TextAreaFormGroup.
   */
  onChange: PT.func,
  /**
   * Labels are required for accessibility, but can be hidden visually.
   */
  hideLabel: PT.bool,
  /**
   * Needed for accessibility when no other label is associated to the input.
   * See "Hiding labels" above to visually hide the label.
   */
  label: PT.oneOfType([PT.string, PT.node]).isRequired,
  /**
   * Used to render content *before* the label, but outside the click-able area.
   */
  renderLabelStart: PT.oneOfType([PT.func, PT.node]),
  /**
   * Used to render content *after* the label, but outside the click-able area.
   */
  renderLabelEnd: PT.oneOfType([PT.func, PT.node]),
  /**
   * Label and message will be inlined.
   */
  inline: PT.bool,
  /**
   * Some suggested or example information.
   */
  placeholder: PT.string,
  /**
   * Adds a message below the <textarea>, which will update based on error/success/warning.
   */
  // message: PT.oneOfType([PT.string, PT.func]),
  /**
   * Specifies the type of message to be displayed (error, warning, success)
   */
  // statusType: PT.oneOf(['error', 'warning', 'success']),
  /**
   * Use to match container, useful for setting specific widths (by setting width on container).
   */
  fullWidth: PT.bool,
  /**
   * Use to access the <textarea> element ref.
   */
  inputRef: PT.func,
  /**
   * *Prop will be deprecated in a future version, use renderLabelEnd with `<HelpTip/>` instead.*
   * Adds an info tooltip with your help message (can pass a string or node).
   */
  help: PT.oneOfType([PT.string, PT.node]),
  /**
   * A data-id that can be used for writing unit tests.
   */
  messageE2EId: PT.string
};
// copied from packages/storm-ui/src/TextAreaFormGroup/TextAreaFormGroup.tsx
const textAreaDefaultProps = {
  disabled: false,
  onChange: noop,
  hideLabel: false,
  renderLabelStart: undefined,
  renderLabelEnd: undefined,
  inline: false,
  placeholder: '',
  // message: '',
  // statusType: undefined,
  fullWidth: false,
  help: undefined,
  inputRef: () => null,
  messageE2EId: undefined,
  className: ''
};

// @TODO: update message
const defaultRenderMessage = (characters, maxGlyphCount) => {
  const charactersRemaining = maxGlyphCount - characters;
  return /*#__PURE__*/React__default.createElement(InlineMessage, {
    messageType: charactersRemaining < 0 ? 'error' : undefined,
    message: charactersRemaining >= 0 ? `${charactersRemaining} characters remaining` : `Delete ${Math.abs(charactersRemaining)} characters.`
  });
};
const TextAreaWithCharacterCount = ({
  id,
  maxGlyphCount,
  getCharactersCount: getCharactersCount$1 = getCharactersCount,
  renderMessage = defaultRenderMessage,
  value,
  statusType,
  taktId,
  taktValue,
  ...restProps
}) => {
  const uid = useUID(id);
  const characters = getCharactersCount$1(['string', 'number'].includes(typeof value) ? `${value}` : '');
  const isErrorStatus = maxGlyphCount - characters < 0 || statusType === 'error';
  return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('text-area-with-character-count')
  }, () => /*#__PURE__*/React__default.createElement(TextAreaFormGroup, _extends({}, restProps, {
    value: value,
    id: uid,
    message: () => renderMessage(characters, maxGlyphCount),
    statusType: isErrorStatus ? 'error' : undefined
  })));
};
TextAreaWithCharacterCount.propTypes = {
  ...textAreaPropTypes,
  maxGlyphCount: PT.number.isRequired,
  getCharactersCount: PT.func,
  renderMessage: PT.func
};
TextAreaWithCharacterCount.defaultProps = {
  ...textAreaDefaultProps,
  getCharactersCount: getCharactersCount,
  renderMessage: defaultRenderMessage
};

export { TextAreaWithCharacterCount as T };
//# sourceMappingURL=TextAreaWithCharacterCount-JpWVe5yb.js.map
